import { useState } from 'react';

import { useAppConfig } from '../state/config';
import { createGap, patchGap } from '../state/gaps';
import { useProfile } from '../state/profile';
import type { Gap } from '../types';
import { GEO_ALL } from '../types';

import { Modal } from './Modal';

/**
 * "New form required" composer — also handles editing an existing
 * new_form_required gap when ``existing`` is provided. The description is
 * persisted as two labelled sections (``Required functionality`` + ``Why is
 * this form needed?``) so edit can split it back into two textareas.
 */
export function NewFormRequiredDialog(
  props: {
    onClose: () => void;
    onSaved: () => void;
  } & (
    | { existing: Gap; stream?: never; topBarGeo?: never }
    | { existing?: undefined; stream: string; topBarGeo: string }
  ),
) {
  const { existing, onClose, onSaved } = props;
  const cfg = useAppConfig();
  const { profile } = useProfile();
  const isEdit = !!existing;

  const stream = existing?.stream ?? props.stream!;
  const firstGeoId = cfg.geographies[0]?.id ?? '';
  const initialGeo =
    existing?.geography ??
    (props.topBarGeo && props.topBarGeo !== GEO_ALL ? props.topBarGeo : firstGeoId);

  const split = splitDescription(existing?.description ?? '');
  const [geography, setGeography] = useState(initialGeo);
  const [proposed, setProposed] = useState(existing?.proposed_form_name ?? '');
  const [purpose, setPurpose] = useState(split.purpose);
  const [reason, setReason] = useState(split.reason);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!geography) return setError('Please pick a geography.');
    if (!proposed.trim()) return setError('Proposed form name is required.');
    if (!purpose.trim())
      return setError(
        'Detailed description of required functionality is required.',
      );
    if (!reason.trim())
      return setError('Please explain why this form is needed.');
    setSaving(true);
    try {
      const description =
        `Required functionality:\n${purpose.trim()}\n\n` +
        `Why is this form needed?\n${reason.trim()}`;
      if (isEdit) {
        await patchGap(existing.id, {
          geography,
          proposed_form_name: proposed.trim(),
          description,
        });
      } else {
        await createGap({
          scope: 'stream',
          stream,
          geography,
          gap_type: 'new_form_required',
          proposed_form_name: proposed.trim(),
          description,
          author_name: profile?.full_name ?? 'Unknown',
          author_email: profile?.email ?? null,
          kb_number: null,
          form_sys_id: null,
          form_name_snapshot: null,
          field_name: null,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={isEdit ? 'Edit: New form required' : 'New form required'}
      subtitle={
        <>
          Stream <span className="font-semibold">{stream}</span>
        </>
      }
      size="xl"
      onClose={saving ? undefined : onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        {!isEdit && (
          <p className="text-sm text-slate-600">
            Flag a new intake form that should exist for{' '}
            <span className="font-semibold">{stream}</span>. This appears on the
            stream-level board under <em>New form required</em>.
          </p>
        )}

        <div className="space-y-1">
          <label className="label">Geography</label>
          <select
            className="input text-sm"
            value={geography}
            onChange={(e) => setGeography(e.target.value)}
          >
            {cfg.geographies.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="label">Proposed form name</label>
          <input
            autoFocus
            className="input text-sm"
            value={proposed}
            onChange={(e) => setProposed(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <label className="label">
            Detailed description of required functionality
          </label>
          <textarea
            className="input text-sm min-h-[120px]"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <label className="label">Why is this form needed?</label>
          <textarea
            className="input text-sm min-h-[96px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost h-9"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary h-9" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create gap'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Best-effort split of a ``new_form_required`` description into its two
 * labelled sections. Accepts both the current header ("Required
 * functionality:") and the earlier one ("Purpose and required fields:") that
 * was used before the label rename. Non-matching input (e.g. free-form edits
 * via the old inline editor) falls back to putting everything in ``purpose``.
 */
function splitDescription(desc: string): { purpose: string; reason: string } {
  if (!desc) return { purpose: '', reason: '' };
  const m = desc.match(
    /^(?:Required functionality|Purpose and required fields):\n([\s\S]*?)\n\nWhy is this form needed\?\n([\s\S]*)$/,
  );
  if (m) return { purpose: m[1], reason: m[2] };
  return { purpose: desc, reason: '' };
}
